import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { ACCOUNTANT_TABS, AccountantScreen, type AccountantTab } from './accountant/Screens.js';
import { useOrgData } from './accountant/useOrgData.js';
import type { Api, Organisation } from './api.js';
import { Bills } from './bills/Bills.js';
import { Dashboard, Insights } from './Dashboard.js';
import { DataPanel } from './DataPanel.js';
import { BootScreen } from './Loader.js';
import { Brand } from './Logo.js';
import { Money } from './money/Money.js';
import { OperatorConsole } from './Operator.js';
import { PeoplePanel } from './People.js';
import { PlanBanner, PlanPanel } from './PlanPanel.js';
import { Reports } from './reports/Reports.js';
import { Setup } from './Setup.js';
import { type Item, Todo, waiting } from './Todo.js';
import { Tax } from './tax/Tax.js';

export function Home({ api, onSignOut }: { api: Api; onSignOut: () => void }) {
  const [orgs, setOrgs] = useState<Organisation[] | null>(null);
  const [operator, setOperator] = useState(false);
  const [operatorView, setOperatorView] = useState(false);
  // An organisation the operator opened with the owner's support grant (read-only).
  const [supporting, setSupporting] = useState<Organisation | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const me = await api.me();
      setOrgs(me.organisations);
      setOperator(me.operator === true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your books.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { id } = await api.createOrganisation(name);
      try {
        sessionStorage.setItem('foderis.setup', id);
      } catch {
        // Private browsing: the setup questions can be answered later in Tax and Home.
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not set up your business.');
    } finally {
      setBusy(false);
    }
  }

  const header = (
    <header className="bar">
      <Brand />
      <button type="button" className="link" onClick={onSignOut}>
        Sign out
      </button>
    </header>
  );

  if (orgs === null)
    return (
      <BootScreen
        {...(error
          ? {
              error,
              onRetry: () => {
                setError('');
                void load();
              },
            }
          : {})}
      />
    );

  if (orgs.length === 0) {
    return (
      <>
        {header}
        <form className="card" onSubmit={create}>
          <h1>Welcome</h1>
          <label>
            What is your business called?
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Setting up' : 'Set up my books'}
          </button>
        </form>
      </>
    );
  }

  if (supporting)
    return (
      <>
        <p className="support-banner">
          Foderis support, read-only: {supporting.name}.{' '}
          <button type="button" className="link" onClick={() => setSupporting(null)}>
            Leave
          </button>
        </p>
        <Books api={api} org={supporting} onRenamed={load} onSignOut={onSignOut} />
      </>
    );
  if (operator && (operatorView || orgs.length === 0))
    return (
      <>
        {header}
        <main className="wide">
          {orgs.length > 0 && (
            <button type="button" className="link" onClick={() => setOperatorView(false)}>
              Back to my books
            </button>
          )}
          <OperatorConsole
            api={api}
            onOpen={(id, name) => setSupporting({ id, name, role: 'support' })}
          />
        </main>
      </>
    );
  const org = orgs[0] as Organisation;
  return (
    <Books
      api={api}
      org={org}
      onRenamed={load}
      onSignOut={onSignOut}
      {...(operator ? { onOperator: () => setOperatorView(true) } : {})}
    />
  );
}

type Tab = 'home' | 'money' | 'bills' | 'reports' | 'tax' | 'settings' | AccountantTab;

function readToggle(): boolean | null {
  try {
    const v = localStorage.getItem('foderis.accountantView');
    return v === null ? null : v === 'on';
  } catch {
    return null;
  }
}

function Books(props: {
  api: Api;
  org: Organisation;
  onRenamed: () => void;
  onSignOut: () => void;
  /** Foderis operators only: back to the operator console. */
  onOperator?: () => void;
}) {
  const { api, org } = props;
  // Accountant roles always see the accountant view; owners can switch it on (BRIEF.md section 1).
  const isAccountant = org.role.startsWith('accountant');
  const [accountantView, setAccountantView] = useState(isAccountant || readToggle() === true);
  const [tab, setTab] = useState<Tab>('home');
  const [setup, setSetup] = useState(() => {
    try {
      return sessionStorage.getItem('foderis.setup') === org.id;
    } catch {
      return false;
    }
  });
  const finishSetup = () => {
    try {
      sessionStorage.removeItem('foderis.setup');
    } catch {
      // Nothing stored.
    }
    setSetup(false);
  };

  function toggle(on: boolean) {
    setAccountantView(on);
    if (!on) setTab('home');
    try {
      localStorage.setItem('foderis.accountantView', on ? 'on' : 'off');
    } catch {
      // Private browsing: the choice lasts for this visit only.
    }
  }

  const todo = useOrgData<{ items: Item[] }>(api, org.id, '/todo');
  const reloadTodo = todo.reload;
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh the counts on every screen change
  useEffect(() => {
    void reloadTodo();
  }, [tab, reloadTodo]);
  const counts: Partial<Record<Tab, number>> = todo.data ? waiting(todo.data.items) : {};

  const navButton = ([id, label]: readonly [Tab, string]) => (
    <button
      key={id}
      type="button"
      className={tab === id ? 'tab active' : 'tab'}
      aria-current={tab === id ? 'page' : undefined}
      onClick={() => setTab(id)}
    >
      {label}
      {(counts[id] ?? 0) > 0 && (
        <span className={id === 'tax' ? 'count gold' : 'count'}>
          {counts[id]}
          <span className="sr-only"> waiting</span>
        </span>
      )}
    </button>
  );

  return (
    <div className="shell">
      <aside className="sidebar">
        <Brand />
        <div className="org-chip">
          <span className="eyebrow">Business</span>
          <strong>{org.name}</strong>
        </div>
        <nav className="side-nav" aria-label="Main">
          {MAIN_TABS.map(navButton)}
        </nav>
        {accountantView && (
          <nav className="side-nav" aria-label="Accountant">
            <span className="nav-label">Accountant</span>
            {ACCOUNTANT_TABS.map(navButton)}
          </nav>
        )}
        <div className="side-foot">
          {navButton(['settings', 'Settings'])}
          {props.onOperator && (
            <button type="button" className="tab" onClick={props.onOperator}>
              Operator console
            </button>
          )}
          {!isAccountant && (
            <label className="inline muted tab">
              <input
                type="checkbox"
                checked={accountantView}
                onChange={(e) => toggle(e.target.checked)}
              />
              Accountant view
            </label>
          )}
          <button type="button" className="tab" onClick={props.onSignOut}>
            Sign out
          </button>
        </div>
      </aside>
      <main className="content">
        <div className="page">
          {tab === 'home' && (
            <>
              <h1>Home</h1>
              <PlanBanner api={api} orgId={org.id} />
              {setup && <Setup api={api} orgId={org.id} onDone={finishSetup} />}
              <Dashboard api={api} orgId={org.id} open={setTab} />
              <Todo
                api={api}
                orgId={org.id}
                onOpenMoney={() => setTab('money')}
                onOpenTax={() => setTab('tax')}
              />
              <Insights api={api} orgId={org.id} />
            </>
          )}
          {tab === 'settings' && (
            <div className="stack settings">
              <h1>Settings</h1>
              <section>
                <BusinessName api={api} org={org} onRenamed={props.onRenamed} />
              </section>
              <section>
                <PeoplePanel api={api} orgId={org.id} role={org.role} />
              </section>
              <section>
                <PlanPanel api={api} orgId={org.id} />
              </section>
              <section>
                <DataPanel api={api} org={org} />
              </section>
            </div>
          )}
          {tab === 'money' && <Money api={api} orgId={org.id} />}
          {tab === 'bills' && <Bills api={api} orgId={org.id} />}
          {tab === 'reports' && <Reports api={api} orgId={org.id} />}
          {tab === 'tax' && <Tax api={api} orgId={org.id} />}
          {isAccountantTab(tab) && <AccountantScreen tab={tab} api={api} orgId={org.id} />}
        </div>
      </main>
    </div>
  );
}

const MAIN_TABS = [
  ['home', 'Home'],
  ['money', 'Money'],
  ['bills', 'Bills'],
  ['reports', 'Reports'],
  ['tax', 'Tax'],
] as const;

function isAccountantTab(tab: Tab): tab is AccountantTab {
  return ACCOUNTANT_TABS.some(([id]) => id === tab);
}

function BusinessName({
  api,
  org,
  onRenamed,
}: {
  api: Api;
  org: Organisation;
  onRenamed: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(org.name);
  const [error, setError] = useState('');
  const canRename = org.role === 'owner' || org.role === 'admin';

  async function save(event: FormEvent) {
    event.preventDefault();
    setError('');
    try {
      await api.renameBusiness(org.id, name);
      setEditing(false);
      onRenamed();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not rename the business.');
    }
  }

  if (!editing) {
    return (
      <div className="title-row">
        <h1>{org.name}</h1>
        {canRename && (
          <button type="button" className="link" onClick={() => setEditing(true)}>
            Rename
          </button>
        )}
      </div>
    );
  }
  return (
    <form onSubmit={save} className="stack">
      <label>
        Business name
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required />
      </label>
      {error && <p className="error">{error}</p>}
      <div className="title-row">
        <button type="submit">Save</button>
        <button type="button" className="link" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
